// pages/Products/Products.js
import React, { useState, useEffect, useCallback } from 'react';
import ProductCard from '../../components/ProductCard/ProductCard';
import productAPI from '../../services/productApi';
import { useCart } from '../../contexts/CartContext';
import './Products.css';

const Products = ({ setCurrentPage, setPageData }) => {
  const { addToCart, getItemQuantity, formatPrice } = useCart();

  // State management
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPageState] = useState(1);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [addingToCart, setAddingToCart] = useState({});
  const [addToCartMessage, setAddToCartMessage] = useState('');

  // Comparison state
  const [compareList, setCompareList] = useState([]);
  const [compareError, setCompareError] = useState('');

  // Default categories structure
  const defaultCategories = [
    { id: 'all', name: 'All Products', icon: '🌐' },
    { id: 'cameras', name: 'Security Cameras', icon: '📹' },
    { id: 'nvr', name: 'NVR Systems', icon: '💾' },
    { id: 'phones', name: 'Communication', icon: '📞' }
  ];

  const sortOptions = [
    { value: 'name', label: 'Name (A-Z)' },
    { value: 'price-low', label: 'Price (Low to High)' },
    { value: 'price-high', label: 'Price (High to Low)' }
  ];

  // Navigation handler for product details
  const handleProductClick = (productId) => {
    setPageData({
      itemId: productId,
      itemType: 'product'
    });
    setCurrentPage('details');
  };

  // Handle Add to Cart
  const handleAddToCart = async (product, quantity = 1, e) => {
    if (e) {
      e.stopPropagation();
    }

    const productId = product.id;
    setAddingToCart(prev => ({ ...prev, [productId]: true }));
    setAddToCartMessage('');

    try {
      const cartItem = {
        id: product.id,
        title: product.title,
        price: product.price,
        image: product.image,
        type: 'product',
        description: product.description
      };

      const success = addToCart(cartItem, quantity);

      if (success) {
        setAddToCartMessage(`Added ${quantity} x ${product.title} to cart!`);

        setTimeout(() => {
          setAddToCartMessage('');
        }, 3000);
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      setAddToCartMessage(error.message || 'Failed to add item to cart');

      setTimeout(() => {
        setAddToCartMessage('');
      }, 3000);
    } finally {
      setAddingToCart(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleViewCart = () => {
    setCurrentPage('cart');
  };

  // Toggle compare selection
  const toggleCompare = (product) => {
    setCompareError('');
    setCompareList(prev => {
      const isSelected = prev.some(p => p.id === product.id);

      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else {
        if (prev.length >= 2) {
          setCompareError('You can only compare 2 products at a time.');
          setTimeout(() => setCompareError(''), 3000);
          return prev;
        }
        return [...prev, product];
      }
    });
  };

  // Handle compare navigation
  const handleCompare = () => {
    if (compareList.length === 2) {
      setPageData({
        products: compareList
      });
      setCurrentPage('product-comparison');
    }
  };

  // Clear comparison list
  const clearCompare = () => {
    setCompareList([]);
    setCompareError('');
  };

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await productAPI.getCategories({
        include_counts: true,
        active_only: true
      });

      if (response && response.success && response.data && response.data.categories) {
        const apiCategories = response.data.categories.map(cat => ({
          id: cat.id,
          name: cat.name,
          icon: getCategoryIcon(cat.name),
          count: cat.product_count || 0
        }));

        const allCategories = [
          {
            id: 'all',
            name: 'All Products',
            icon: '🌐',
            count: apiCategories.reduce((sum, cat) => sum + cat.count, 0)
          },
          ...apiCategories
        ];

        setCategories(allCategories);
      } else {
        setCategories(defaultCategories);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories(defaultCategories);
    }
  };

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        per_page: 12
      };

      // Only add category_id if it's not 'all' and it's a valid value
      if (selectedCategory && selectedCategory !== 'all') {
        params.category_id = selectedCategory;
      }

      // Only add search if there's actual content
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await productAPI.getProducts(params);

      if (response && response.success && response.data) {
        setProducts(response.data.products || []);
        setPagination(response.data.pagination || null);
      } else {
        const errorMsg = response?.message || response?.error || 'Failed to load products';
        setError(errorMsg);
        setProducts([]);
      }
    } catch (error) {
      let errorMessage = 'Failed to load products';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      const errorInfo = productAPI.handleAPIError ? productAPI.handleAPIError(error) : { message: errorMessage };
      setError(errorInfo.message || errorMessage);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, currentPage, searchTerm]);

  // FIXED: Debounced search effect - proper implementation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Reset to page 1 when search term changes
      if (currentPage !== 1) {
        setCurrentPageState(1);
      } else {
        // Only load if we're already on page 1
        loadProducts();
      }
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  // FIXED: Load products when category or page changes
  useEffect(() => {
    loadProducts();
  }, [selectedCategory, currentPage]);

  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes('camera') || name.includes('security')) return '📹';
    if (name.includes('nvr') || name.includes('recorder') || name.includes('storage')) return '💾';
    if (name.includes('phone') || name.includes('communication') || name.includes('voip')) return '📞';
    if (name.includes('access') || name.includes('control')) return '🔐';
    if (name.includes('alarm') || name.includes('sensor')) return '🚨';
    return '🔧';
  };

  const transformProduct = (product) => {
    console.log('Transforming product:', product);

    // Better image handling with API processed URLs
    let imageUrl = null;

    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      imageUrl = product.images[0];
    } else if (product.primary_image) {
      imageUrl = product.primary_image;
    } else if (product.image) {
      imageUrl = product.image;
    } else if (product.image_url) {
      imageUrl = product.image_url;
    }

    // Proper category extraction from API structure
    let categoryId = null;
    let categoryName = null;

    if (product.category && typeof product.category === 'object') {
      categoryId = product.category.id;
      categoryName = product.category.name;
    } else if (product.category_id) {
      categoryId = product.category_id;
    }

    // Handle pricing with sale logic
    const isOnSale = product.is_on_sale && product.sale_price && product.sale_price < product.price;
    const currentPrice = isOnSale ? product.sale_price : product.price;
    const originalPrice = isOnSale ? product.price : null;
    const discount = isOnSale ? productAPI.calculateDiscount(product.price, product.sale_price) : 0;

    // Proper stock status from track_inventory and stock_quantity
    let inStock = true;
    if (product.track_inventory) {
      inStock = product.stock_quantity > 0;
    }

    const transformedProduct = {
      id: product.id,
      title: product.name || product.title,
      category: categoryId,
      categoryName: categoryName,
      description: product.description || product.short_description || '',
      image: imageUrl,
      price: currentPrice,
      originalPrice: originalPrice,
      inStock: inStock,
      stockQuantity: product.stock_quantity,
      trackInventory: product.track_inventory,
      features: Array.isArray(product.features) ? product.features : [],
      tags: Array.isArray(product.tags) ? product.tags : [],
      isOnSale: isOnSale,
      discount: discount,
      slug: product.slug,
      meta_title: product.meta_title,
      meta_description: product.meta_description,
      is_featured: product.is_featured || false,
      is_active: product.is_active || false
    };

    console.log('Transformed product:', transformedProduct);
    return transformedProduct;
  };

  const getSortedProducts = () => {
    const transformedProducts = products.map(transformProduct);

    return [...transformedProducts].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.title.localeCompare(b.title);
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        default:
          return 0;
      }
    });
  };

  const sortedProducts = getSortedProducts();

  const handleCategoryChange = (categoryValue) => {
    setSelectedCategory(categoryValue);
    setCurrentPageState(1);
  };

  const handleSortChange = (sortValue) => {
    setSortBy(sortValue);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPageState(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const PaginationControls = () => {
    if (!pagination || pagination.pages <= 1) return null;

    const pages = [];
    const currentPageNum = pagination.page;
    const totalPages = pagination.pages;

    for (let i = Math.max(1, currentPageNum - 2); i <= Math.min(totalPages, currentPageNum + 2); i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-controls" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.5rem',
        marginTop: '2rem',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => handlePageChange(currentPageNum - 1)}
          disabled={currentPageNum === 1}
          className="btn btn-secondary"
          style={{ opacity: currentPageNum === 1 ? 0.5 : 1 }}
        >
          Previous
        </button>

        {pages.map(page => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`btn ${page === currentPageNum ? 'btn-primary' : 'btn-secondary'}`}
            style={{ minWidth: '40px' }}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => handlePageChange(currentPageNum + 1)}
          disabled={currentPageNum === totalPages}
          className="btn btn-secondary"
          style={{ opacity: currentPageNum === totalPages ? 0.5 : 1 }}
        >
          Next
        </button>

        <span style={{ marginLeft: '1rem', fontSize: '0.9rem', color: '#666' }}>
          Page {currentPageNum} of {totalPages} ({pagination.total || 0} total products)
        </span>
      </div>
    );
  };

  if (loading && products.length === 0) {
    return (
      <div className="products-page">
        <section className="products-header">
          <div className="container">
            <h1 className="page-title fade-in">Professional Security & Communication Equipment</h1>
            <p className="page-subtitle fade-in">Loading products...</p>
          </div>
        </section>
        <div className="loading-spinner" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4rem',
          minHeight: '400px'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '6px solid #e0e0e0',
            borderTop: '6px solid #2196F3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '1rem'
          }}></div>
          <p>Loading products...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="products-page">
      {/* Add to Cart Success/Error Message */}
      {addToCartMessage && (
        <div className={`cart-message ${addToCartMessage.includes('Added') ? 'success' : 'error'}`} style={{
          position: 'fixed',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          backgroundColor: addToCartMessage.includes('Added') ? '#4caf50' : '#f44336',
          color: 'white',
          padding: '1rem 2rem',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          animation: 'slideDown 0.3s ease'
        }}>
          <span>{addToCartMessage}</span>
          {addToCartMessage.includes('Added') && (
            <button
              className="btn btn-sm"
              onClick={handleViewCart}
              style={{
                backgroundColor: 'white',
                color: '#4caf50',
                padding: '0.5rem 1rem',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              View Cart
            </button>
          )}
        </div>
      )}

      {/* Page Header */}
      <section className="products-header">
        <div className="container">
          <h1 className="page-title fade-in">Professional Security & Communication Equipment</h1>
          <p className="page-subtitle fade-in">
            Industry-leading surveillance cameras, NVR systems, and communication devices.
            Trusted by professionals worldwide for reliable security solutions.
          </p>
        </div>
      </section>

      {/* Search Bar */}
      <section className="products-search">
        <div className="container">
          <div className="search-wrapper" style={{
            display: 'flex',
            maxWidth: '500px',
            margin: '0 auto 2rem',
            position: 'relative'
          }}>
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                border: '1px solid #ddd',
                borderRadius: '4px 0 0 4px',
                fontSize: '1rem'
              }}
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                style={{
                  position: 'absolute',
                  right: '60px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  color: '#999'
                }}
                title="Clear search"
              >
                ×
              </button>
            )}
            <button
              className="search-button btn btn-primary"
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '0 4px 4px 0',
                border: 'none'
              }}
            >
              🔍
            </button>
          </div>
        </div>
      </section>

      {/* Error Message */}
      {error && (
        <section className="error-message">
          <div className="container">
            <div className="alert alert-error" style={{
              background: '#fee',
              color: '#c33',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              textAlign: 'center',
              border: '2px solid #fcc'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
              <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Error Loading Products</p>
              <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>{error}</p>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={loadProducts}
                  className="btn btn-primary"
                >
                  🔄 Try Again
                </button>

                {(searchTerm || selectedCategory !== 'all') && (
                  <>
                    {searchTerm && (
                      <button
                        onClick={() => {
                          clearSearch();
                          setError(null);
                        }}
                        className="btn btn-secondary"
                      >
                        Clear Search
                      </button>
                    )}
                    {selectedCategory !== 'all' && (
                      <button
                        onClick={() => {
                          handleCategoryChange('all');
                          setError(null);
                        }}
                        className="btn btn-secondary"
                      >
                        View All Categories
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Filters and Sorting */}
      <section className="products-controls">
        <div className="container">
          <div className="controls-wrapper">
            {/* Category Filter */}
            <div className="filter-section">
              <h3 className="control-title">Categories</h3>
              <div className="filter-tabs">
                {categories.map(category => (
                  <button
                    key={category.id}
                    className={`filter-tab ${selectedCategory === category.id ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(category.id)}
                  >
                    <span className="tab-icon">{category.icon}</span>
                    <span className="tab-label">
                      {category.name}
                      {category.count !== undefined && (
                        <span className="category-count" style={{
                          marginLeft: '0.5rem',
                          fontSize: '0.8rem',
                          opacity: 0.7
                        }}>
                          ({category.count})
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Sort Options */}
            <div className="sort-section">
              <h3 className="control-title">Sort by</h3>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="results-info">
            <span className="results-count">
              {loading && <span>Loading... </span>}
              Showing {sortedProducts.length} products
              {pagination && pagination.total && (
                <span> of {pagination.total} total</span>
              )}
              {searchTerm && (
                <span> for "{searchTerm}"</span>
              )}
            </span>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="products-grid section">
        <div className="container">
          {loading && products.length > 0 && (
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <span style={{ color: '#666' }}>⏳ Loading...</span>
            </div>
          )}

          <div className="grid-3">
            {sortedProducts.map(transformedProduct => (
              <ProductCard
                key={transformedProduct.id}
                product={transformedProduct}
                onProductClick={() => handleProductClick(transformedProduct.id)}
                onAddToCart={(quantity, e) => handleAddToCart(transformedProduct, quantity, e)}
                addingToCart={addingToCart[transformedProduct.id]}
                cartQuantity={getItemQuantity(transformedProduct.id, 'product')}
                isSelected={compareList.some(p => p.id === transformedProduct.id)}
                onToggleCompare={toggleCompare}
              />
            ))}
          </div>

          {sortedProducts.length === 0 && !loading && (
            <div className="no-products" style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
              <h3>No products found</h3>
              <p style={{ color: '#666', marginBottom: '1rem' }}>
                {searchTerm
                  ? `No products match your search for "${searchTerm}"`
                  : selectedCategory !== 'all'
                    ? 'No products found in this category'
                    : 'No products available'
                }
              </p>
              {(searchTerm || selectedCategory !== 'all') && (
                <div>
                  {searchTerm && (
                    <button
                      onClick={clearSearch}
                      className="btn btn-secondary"
                      style={{ marginRight: '0.5rem' }}
                    >
                      Clear Search
                    </button>
                  )}
                  {selectedCategory !== 'all' && (
                    <button
                      onClick={() => handleCategoryChange('all')}
                      className="btn btn-primary"
                    >
                      View All Products
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          <PaginationControls />
        </div>
      </section>

      {/* Comparison Floating Bar */}
      {compareList.length > 0 && (
        <div className="compare-bar fade-in-up" style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          borderRadius: '12px',
          padding: '1rem 2rem',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '2rem',
          maxWidth: '90%',
          width: 'auto',
          border: '1px solid #e0e0e0'
        }}>
          <div className="compare-items" style={{ display: 'flex', gap: '1rem' }}>
            {compareList.map(item => (
              <div key={item.id} className="compare-item-preview" style={{ position: 'relative' }}>
                <img
                  src={item.image || '/images/placeholder.jpg'}
                  alt={item.title}
                  style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                />
                <button
                  onClick={() => toggleCompare(item)}
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    background: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            {compareList.length < 2 && (
              <div style={{
                width: '50px',
                height: '50px',
                border: '2px dashed #ddd',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: '0.8rem',
                backgroundColor: '#f9f9f9'
              }}>
                +1
              </div>
            )}
          </div>

          <div className="compare-actions" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 'bold' }}>Compare Products</span>
              <span style={{ fontSize: '0.8rem', color: '#666' }}>
                {compareList.length} of 2 selected
              </span>
            </div>

            <button
              className="btn btn-primary"
              disabled={compareList.length < 2}
              onClick={handleCompare}
              style={{ opacity: compareList.length < 2 ? 0.5 : 1 }}
            >
              Compare Now
            </button>

            <button
              className="btn btn-secondary"
              onClick={clearCompare}
            >
              Clear
            </button>
          </div>

          {compareError && (
            <div style={{
              position: 'absolute',
              top: '-50px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#333',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              animation: 'fadeIn 0.3s ease'
            }}>
              {compareError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Products;